/**
 * Converts a LandingPage node tree's per-node style buckets
 * (wide / desktop / tablet / mobile / small) into real CSS with actual
 * @media queries for the guest-facing renderer. Real devices connecting to
 * captive portal WiFi are at unknown, uncontrolled viewport widths, so true
 * responsive behavior needs actual CSS media queries — a JS-computed "pick
 * one breakpoint's inline style" approach would not adapt to the guest's
 * real browser window.
 *
 * desktop is the unqualified base rule; wide is a min-width query on top of
 * it, and tablet/mobile/small layer on as max-width overrides, narrowest last
 * (see tokens.js). The builder's live preview runs this same CSS inside an
 * iframe of the chosen width, so it shows exactly what a device of that width
 * would.
 */
import { BREAKPOINT_QUERIES, AUTO_STACK_MAX_WIDTH } from '../LandingBuilder/tokens';

function camelToKebab(key) {
    return key.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

// These values are written straight into a <style> element, so anything that
// could end the declaration or open a new rule / at-rule is refused here, even
// though the backend already screens the obvious script schemes: a value like
// `red; } body { display: none` must stay one harmless (invalid) value, never
// become extra CSS. Real values — colours, lengths, var(--x), gradients,
// url("/img/x.png") — contain none of these characters.
const UNSAFE_CSS_CHARS = /[{};<>@\\\n\r]/;
const SAFE_PROPERTY_NAME = /^[a-zA-Z][a-zA-Z0-9]*$/;

export function isSafeCssValue(value) {
    return typeof value === 'number' || (typeof value === 'string' && !UNSAFE_CSS_CHARS.test(value));
}

function declarationsFor(styleObj) {
    return Object.entries(styleObj || {})
        .filter(([k, v]) => v !== undefined && v !== null && v !== '' && SAFE_PROPERTY_NAME.test(k) && isSafeCssValue(v))
        .map(([k, v]) => `${camelToKebab(k)}: ${v};`)
        .join(' ');
}

// A node id ends up inside a CSS selector, so only plain identifier
// characters are ever emitted (the builder's own ids, and every seeded
// template's, already are).
const SAFE_NODE_ID = /^[A-Za-z0-9_-]+$/;

/** Collects every node in the tree that has a `style` object, recursively. */
function collectStyledNodes(nodes, acc = []) {
    for (const node of nodes || []) {
        if (SAFE_NODE_ID.test(String(node.id)) && node.style && BUCKET_ORDER.some((bucket) => node.style[bucket])) {
            acc.push(node);
        }
        if (node.children) collectStyledNodes(node.children, acc);
    }
    return acc;
}

// Cascade order: the base (desktop) rules first, then each media-query bucket
// in the order it should win — wide on big screens, and tablet -> mobile ->
// small overriding one another as the screen narrows.
const BUCKET_ORDER = ['desktop', 'wide', 'tablet', 'mobile', 'small'];

// ── Automatic layout ──────────────────────────────────────────────────
// A design made for desktop should fit every device WITHOUT the author setting
// each one by hand. The layout rules that make that happen — free-placed items
// stacking, rows becoming columns, grids becoming one column — are written ONCE,
// as a single fixed @media block in Landing.css (search "Automatic layout"); the
// renderer only tags nodes with the classes it targets (lp-free / lp-floating /
// lp-natural / lp-row / lp-grid).
//
// The two things that DO depend on each node's own values are generated here,
// inside that same width (AUTO_STACK_MAX_WIDTH), and placed after the wide/tablet
// rules but before mobile/small — so anything the author set explicitly for a phone
// still wins:
//   - reading order: when a free container stacks, its floating children keep the
//     order they sit in on the desktop (top, then left)
//   - fluid text: a big desktop font size scales down smoothly with the screen

const isFloatingNode = (node) => node.style?.desktop?.position === 'absolute';
const readingOrder = (a, b) => {
    const topA = parseFloat(a.style.desktop.top) || 0;
    const topB = parseFloat(b.style.desktop.top) || 0;
    if (topA !== topB) return topA - topB;
    return (parseFloat(a.style.desktop.left) || 0) - (parseFloat(b.style.desktop.left) || 0);
};

const FLUID_MIN_FONT_PX = 18; // smaller text is left alone
const pxOf = (value) => {
    const match = /^\s*(\d+(?:\.\d+)?)px\s*$/.exec(String(value ?? ''));
    return match ? Number(match[1]) : null;
};

// font-size that equals the desktop size at AUTO_STACK_MAX_WIDTH and shrinks in
// proportion below it, never smaller than 55% of the desktop size (and never under 14px).
// A browser too old for clamp() ignores the rule and simply keeps the desktop size.
function fluidFontSize(desktopPx) {
    const floor = Math.max(14, Math.round(desktopPx * 0.55));
    const vw = ((desktopPx / AUTO_STACK_MAX_WIDTH) * 100).toFixed(2);
    return `clamp(${floor}px, ${vw}vw, ${desktopPx}px)`;
}

// Big desktop spacing (an 80px padding around a hero, a 48px gap) would leave a phone
// almost no room for content, so it shrinks the same way text does: equal to the desktop
// value at 860px, proportionally less below it, never under 40% of it (and never under 16px).
// Only positive px lengths of 24px and up are touched; small spacing, rem/%, negative
// margins and anything with a function in it (calc, var) are left exactly as written.
const FLUID_MIN_SPACE_PX = 24;
const SPACING_PROPS = [
    'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
    'gap', 'rowGap', 'columnGap',
];

function fluidSpace(desktopPx) {
    const floor = Math.max(16, Math.round(desktopPx * 0.4));
    const vw = ((desktopPx / AUTO_STACK_MAX_WIDTH) * 100).toFixed(2);
    return `clamp(${floor}px, ${vw}vw, ${desktopPx}px)`;
}

// The value with every big px length made fluid, or null when nothing in it needs to change.
function fluidSpacing(value) {
    const text = String(value ?? '').trim();
    if (!text || text.includes('(') || !isSafeCssValue(text)) return null;
    let changed = false;
    const tokens = text.split(/\s+/).map((token) => {
        const px = pxOf(token);
        if (px === null || px < FLUID_MIN_SPACE_PX) return token;
        changed = true;
        return fluidSpace(px);
    });
    return changed ? tokens.join(' ') : null;
}

// A fixed-height block (a 560px hero) keeps its proportions instead: it may grow to fit its
// content, but never stays taller than the screen can use. Containers laid out "free" are
// handled by the fixed block in Landing.css, and "free-manual" ones are the author's own.
const FLUID_MIN_HEIGHT_PX = 200;
export const CONTAINER_TYPES = ['section', 'container', 'row', 'column', 'grid', 'background'];

function fluidMinHeight(desktopPx) {
    const floor = Math.max(160, Math.round(desktopPx * 0.5));
    const vw = ((desktopPx / AUTO_STACK_MAX_WIDTH) * 100).toFixed(2);
    return `clamp(${floor}px, ${vw}vw, ${desktopPx}px)`;
}

// A property the author set on purpose for tablet / mobile / small is theirs — not generated over.
const setForSmallerScreens = (node, prop) => ['tablet', 'mobile', 'small'].some((bucket) => {
    const value = node.style?.[bucket]?.[prop];
    return value !== undefined && value !== null && value !== '';
});

function autoDeclarations(node) {
    const desktop = node.style?.desktop;
    if (!desktop) return [];
    const declarations = [];
    const add = (prop, value) => declarations.push(`${camelToKebab(prop)}: ${value};`);

    const fontPx = pxOf(desktop.fontSize);
    if (fontPx && fontPx >= FLUID_MIN_FONT_PX && !setForSmallerScreens(node, 'fontSize')) add('fontSize', fluidFontSize(fontPx));

    for (const prop of SPACING_PROPS) {
        const fluid = fluidSpacing(desktop[prop]);
        if (fluid && !setForSmallerScreens(node, prop)) add(prop, fluid);
    }

    const layout = node.props?.layout;
    const heightPx = pxOf(desktop.height);
    if (
        heightPx && heightPx >= FLUID_MIN_HEIGHT_PX && CONTAINER_TYPES.includes(node.type)
        && layout !== 'free' && layout !== 'free-manual'
        && !setForSmallerScreens(node, 'height') && !setForSmallerScreens(node, 'minHeight')
    ) {
        add('height', 'auto');
        add('minHeight', fluidMinHeight(heightPx));
    }
    return declarations;
}

function collectAutoRules(nodes, rules = []) {
    for (const node of nodes || []) {
        const children = node.children || [];
        if (SAFE_NODE_ID.test(String(node.id))) {
            const declarations = autoDeclarations(node);
            if (declarations.length > 0) rules.push(`.${styleClassFor(node)} { ${declarations.join(' ')} }`);
        }
        if (node.props?.layout === 'free' && children.length > 0) {
            children.filter(isFloatingNode).sort(readingOrder).forEach((child, index) => {
                if (SAFE_NODE_ID.test(String(child.id))) rules.push(`.${styleClassFor(child)} { order: ${10 + index}; }`);
            });
        }
        collectAutoRules(children, rules);
    }
    return rules;
}

/**
 * @param {object[]} nodes - the page's node tree (publishedNodes)
 * @returns {string} a full CSS string, safe to drop into a <style> tag. Every
 *   size class becomes a REAL @media query, so any device — whatever its
 *   width — picks up exactly the rules that apply to it.
 */
export function buildResponsiveCss(nodes) {
    const styledNodes = collectStyledNodes(nodes);
    const autoRules = collectAutoRules(nodes);
    if (styledNodes.length === 0 && autoRules.length === 0) return '';

    const rulesByBucket = Object.fromEntries(BUCKET_ORDER.map((bucket) => [bucket, []]));
    for (const node of styledNodes) {
        const selector = `.lp-style-${node.id}`;
        for (const bucket of BUCKET_ORDER) {
            const declarations = declarationsFor(node.style[bucket]);
            if (declarations) rulesByBucket[bucket].push(`${selector} { ${declarations} }`);
        }
    }

    const block = (bucket) => {
        const rules = rulesByBucket[bucket];
        if (rules.length === 0) return '';
        if (bucket === 'desktop') return rules.join('\n');
        return `@media ${BREAKPOINT_QUERIES[bucket]} {\n${rules.join('\n')}\n}`;
    };
    const autoBlock = autoRules.length > 0
        ? `@media (max-width: ${AUTO_STACK_MAX_WIDTH}px) {\n${autoRules.join('\n')}\n}`
        : '';

    // desktop, wide, tablet, AUTOMATIC, then mobile and small (which can still override it).
    return [block('desktop'), block('wide'), block('tablet'), autoBlock, block('mobile'), block('small')]
        .filter(Boolean)
        .join('\n');
}

/** The className a node needs for buildResponsiveCss's generated rules to apply to it. */
export function styleClassFor(node) {
    return `lp-style-${node.id}`;
}

// ── Login-form theme ──────────────────────────────────────────────────
// The login forms are the existing, unmodified components, so the builder
// can't change their markup — but it CAN restyle them. A login node's
// presentational props (labelColor, inputBg, …) become CSS scoped to that
// node's own wrapper (.lp-theme-<id>). Only props that are actually set
// produce a rule, so an untouched login form keeps its normal look.
export const LOGIN_THEME_TYPES = [
    'login-methods', 'pms-login', 'voucher-login', 'otp-login', 'resident-login', 'staff-login', 'login-submit',
];

const TEXT_INPUTS = ['input:not([type="checkbox"]):not([type="radio"])', 'select', 'textarea'];

// [prop, selectors inside the wrapper, CSS properties it sets]
const THEME_RULES = [
    ['labelColor', ['label', '.checkbox-text'], ['color']],
    ['inputBg', TEXT_INPUTS, ['background-color']],
    ['inputColor', TEXT_INPUTS, ['color']],
    ['inputBorder', TEXT_INPUTS, ['border-color']],
    ['inputRadius', TEXT_INPUTS, ['border-radius']],
    ['tabColor', ['.lp-auth-tab'], ['color']],
    ['tabBg', ['.lp-auth-tab'], ['background-color']],
    ['tabActiveColor', ['.lp-auth-tab.active'], ['color', 'border-color']],
    ['linkColor', ['a'], ['color']],
];

// The same props as custom properties on the wrapper, for a whole look (below)
// to read — its own rules are stronger than the ones above, so a colour picked
// in the builder still wins over the look's default through these.
const THEME_VARIABLES = {
    labelColor: '--lp-label-color',
    inputBg: '--lp-input-bg',
    inputColor: '--lp-input-color',
    inputBorder: '--lp-input-border',
    inputRadius: '--lp-input-radius',
    tabColor: '--lp-tab-color',
    tabBg: '--lp-tab-bg',
    tabActiveColor: '--lp-tab-active-color',
    linkColor: '--lp-link-color',
};

/** The className a login node's wrapper needs for buildLoginThemeCss's rules. */
export function themeClassFor(node) {
    return `lp-theme-${node.id}`;
}

// ── Login-box look ────────────────────────────────────────────────────
// A whole design for the login box, written once in Landing.css under
// .lp-look-<name> and picked per node with props.look. 'default' (or none) is
// the classic look, with no extra class.
export const LOGIN_LOOKS = ['default', 'boutique'];

export function lookOf(node) {
    const look = node?.props?.look;
    return LOGIN_LOOKS.includes(look) && look !== 'default' ? look : null;
}

/** The className for a node's look, or '' for the classic one. */
export function lookClassFor(node) {
    const look = lookOf(node);
    return look ? `lp-look-${look}` : '';
}

/** The look of the page's login box — the first login node that has one — or null. */
export function pageLookOf(nodes) {
    for (const node of nodes || []) {
        if (LOGIN_THEME_TYPES.includes(node.type) && lookOf(node)) return lookOf(node);
        const inner = pageLookOf(node.children);
        if (inner) return inner;
    }
    return null;
}

function collectThemedNodes(nodes, acc = []) {
    for (const node of nodes || []) {
        if (LOGIN_THEME_TYPES.includes(node.type) && SAFE_NODE_ID.test(String(node.id))) acc.push(node);
        if (node.children) collectThemedNodes(node.children, acc);
    }
    return acc;
}

/** @returns {string} CSS for every login node's theme props, or '' when none are set. */
export function buildLoginThemeCss(nodes) {
    const rules = [];
    const usable = (value) => typeof value === 'string' && value !== '' && isSafeCssValue(value);
    for (const node of collectThemedNodes(nodes)) {
        for (const [prop, selectors, cssProps] of THEME_RULES) {
            const value = node.props?.[prop];
            if (!usable(value)) continue;
            const selector = selectors.map((sel) => `.${themeClassFor(node)} ${sel}`).join(', ');
            rules.push(`${selector} { ${cssProps.map((p) => `${p}: ${value};`).join(' ')} }`);
        }
        if (lookOf(node)) {
            const variables = Object.entries(THEME_VARIABLES)
                .filter(([prop]) => usable(node.props?.[prop]))
                .map(([prop, variable]) => `${variable}: ${node.props[prop]};`);
            if (variables.length > 0) rules.push(`.${themeClassFor(node)} { ${variables.join(' ')} }`);
        }
    }
    return rules.join('\n');
}
